/**
 * P4 L0 → L1 转换弹框
 * 必传：proof_screenshot_url + wechat_nickname
 * 实时预览 internal_code = KJ+YYYYMMDD+wechat_nickname
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn } from './ui';
import { ScreenshotUpload } from './ScreenshotUpload';

function cleanWechatName(name: string): string {
  return name
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/[^一-龥a-zA-Z0-9_-]/g, '')
    .slice(0, 15);
}

export function AddedWechatModal({
  open, customer, onClose, onDone,
}: {
  open: boolean;
  customer: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    proof_screenshot_url: '',
    avatar_screenshot_url: '',
    wechat_nickname: '',
    real_name: '',
    ai_first_impression: '',
  });
  const [editedCode, setEditedCode] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setForm({ proof_screenshot_url: '', avatar_screenshot_url: '', wechat_nickname: '', real_name: '', ai_first_impression: '' });
      setEditedCode(null);
    }
  }, [open]);

  // 实时预览 internal_code
  const previewCode = useMemo(() => {
    if (editedCode != null) return editedCode;
    const date = dayjs().format('YYYYMMDD');
    const name = cleanWechatName(form.wechat_nickname) || String(customer?.id ?? 0).padStart(4, '0').slice(-4);
    return `KJ+${date}+${name}`;
  }, [form.wechat_nickname, editedCode, customer?.id]);

  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: () => $.post(`/customers/${customer.id}/confirm-wechat-added`, {
      ...form,
      // 销售手改了内部编号 → 传后端存(查唯一)；没改则后端按 KJ+日期+昵称 自动生成(带去重后缀)
      ...(editedCode != null && editedCode.trim() ? { custom_internal_code: editedCode.trim() } : {}),
    }),
    onSuccess: () => {
      message.success('已确认加微信，进入 L1 ✓');
      // v1.5 C-1.a #11 修：失效相关 query，让前端立刻刷新（之前要 F5）
      qc.invalidateQueries({ queryKey: ['customer', String(customer?.id)] }); // batch-fix#H7：归一命名(原 ['customer-detail',..] 与详情页 queryKey 不匹配)
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['follow-ups'] });
      qc.invalidateQueries({ queryKey: ['customer-timeline', customer?.id] });
      onDone();
    },
    onError: (e: any) => message.error(e?.message ?? '提交失败'),
  });

  return (
    <Modal
      title={`✅ 确认加上微信 — ${customer?.nickname ?? '客户'}`}
      open={open}
      onCancel={onClose}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               onClick={() => submit.mutate()}
               disabled={!form.proof_screenshot_url || !form.wechat_nickname || submit.isPending}>
            {submit.isPending ? '提交中…' : '确认并进入 L1'}
          </Btn>
        </>
      }
      width={560}
    >
      <Field label="通过截图（必传 — 粘贴/拖拽/选文件）">
        <ScreenshotUpload value={form.proof_screenshot_url}
                          onChange={(url) => setForm((f) => ({ ...f, proof_screenshot_url: url }))}
                          entityType="customer" entityId={customer?.id ?? 0} fieldName="wechat_proof"
                          enablePaste={open} />
      </Field>
      <Field label="客户微信昵称（必填）">
        <Input autoFocus value={form.wechat_nickname}
               onChange={(e) => setForm({ ...form, wechat_nickname: e.target.value })}
               placeholder="如：张momo / 王大壮" />
      </Field>
      <Field label="内部编号（自动生成，可改）">
        <Input value={previewCode}
               onChange={(e) => setEditedCode(e.target.value)}
               style={{ fontFamily: 'monospace' }} />
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
          格式：KJ+YYYYMMDD+昵称（去 emoji/特殊字符，截 15 字）
        </div>
      </Field>
      <Field label="客户姓名（选填）">
        <Input value={form.real_name}
               onChange={(e) => setForm({ ...form, real_name: e.target.value })} />
      </Field>
      <Field label="头像截图（选填 — 拖拽/选文件）">
        <ScreenshotUpload value={form.avatar_screenshot_url}
                          onChange={(url) => setForm((f) => ({ ...f, avatar_screenshot_url: url }))}
                          entityType="customer" entityId={customer?.id ?? 0} fieldName="wechat_avatar"
                          maxHeight={120} />
      </Field>
      <Field label="第一印象（选填，留空则 AI 生成）">
        <Input.TextArea value={form.ai_first_impression}
                        onChange={(e) => setForm({ ...form, ai_first_impression: e.target.value })}
                        rows={2}
                        placeholder="30 字内特征摘要 / 给后续跟进的提示" />
      </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--text-2)' }}>{label}</div>
      {children}
    </div>
  );
}
